import { observable, action, computed, toJS } from 'mobx';
import { pick } from 'lodash';
import { AppState } from 'react-native';
import { getWarrior, saveWarrior, onWarriorChanged, onWarriorDisconnect } from '../lib/dbCalls';
import {
  increaseParameter as increaseParameterService,
  increaseAbility as increaseAbilityService,
  increaseSkill as increaseSkillService,
  dressUndressThing as dressUndressThingService,
  undressThings as undressThingsService,
  removeThing as removeThingService,
  putInCombat as putInCombatService,
  quitFromCombat as quitFromCombatService,
  applyThing as applyThingService,
  checkWarrior,
} from '../lib/apiCalls';
import {
  getDressedThings,
  setActionTime,
  mapWarrior,
  inBuilding,
  getUndressedThings,
  getAppliedThings,
} from '../utils/warriorUtils';

import appStore from './app';
import combatStore from './combat';
import islandStore from './island';

import { UserType, WarriorType, WarriorThingType } from '../lib/types';

import { compareObj, getDuration } from '../utils/utils';
import { IS_WEB } from '../lib/appConstants';

function getStatus(value: boolean): 'online' | 'offline' {
  return value ? 'online' : 'offline';
}

export async function increaseParameter(name: string) {
  await increaseParameterService(name);
}

export async function increaseAbility(name: string) {
  await increaseAbilityService(name);
}

export async function increaseSkill(id: string) {
  await increaseSkillService(id);
}

export async function removeThing(id: string) {
  await removeThingService(id);
}

export async function dressUndressThing(dress: boolean, id: string) {
  await dressUndressThingService(dress, id);
}

export async function undressThings() {
  await undressThingsService();
}

export async function putInCombat(id: string) {
  await putInCombatService(id);
}

export async function quitFromCombat() {
  await quitFromCombatService();
  combatStore.clear();
}

export async function applyThing(id: string) {
  await applyThingService(id);
}

class Hero {
  @observable
  hero!: WarriorType;

  heroChangedRef: any = {};

  checkTimer: any;

  @computed
  get undressedThings(): WarriorThingType[] {
    return getUndressedThings(this.hero);
  }

  @computed
  get dressedThings(): WarriorThingType[] {
    return getDressedThings(this.hero);
  }

  @action
  async fetch(user: UserType) {
    this.hero = (await getWarrior(user.uid, appStore.initData)) as WarriorType;

    this.setCheckTimer();

    await this.setActionTime();

    this.listenChanges();
    onWarriorDisconnect(user.uid, { status: getStatus(false) });
  }

  listenChanges() {
    this.heroChangedRef = onWarriorChanged(this.hero.id, this.onWarriorChanged);
  }

  @action
  onWarriorChanged = (snapshot: any) => {
    const value = snapshot.val();
    if (!value) return;
    const newHero = mapWarrior(value, appStore.initData);

    if ((AppState.currentState === 'active' || IS_WEB) && newHero.status === 'offline') {
      this.updateStatus(true, true);
      return;
    }

    if (!inBuilding(newHero) && !compareObj(this.hero.location, newHero.location)) {
      islandStore.fetch(newHero.location.coordinateX, newHero.location.coordinateY, true);
    }
    Object.assign(this.hero, newHero);

    this.setCheckTimer();
  };

  setCheckTimer() {
    clearInterval(this.checkTimer);
    const appliedThings = getAppliedThings(this.hero);
    if (!appliedThings.length) return;

    appliedThings.sort((a, b) => a.finishing! - b.finishing!);

    const thing = appliedThings[0];

    const duration = getDuration(thing.finishing!);

    if (!duration) {
      checkWarrior();
      return;
    }

    this.checkTimer = setTimeout(checkWarrior, duration);
  }

  clear() {
    this.heroChangedRef.off('value', this.onWarriorChanged);
  }

  @action
  async saveGeneral(data: Partial<WarriorType>) {
    Object.assign(this.hero, data);
    await this.save(['name', 'about', 'lng']);
  }

  @action
  async moveOnIsland(x: number, y: number) {
    await this.save(['location'], {
      location: { ...this.hero.location, coordinateX: x, coordinateY: y },
    });
  }

  @action
  async moveToBuilding(building: string | undefined | null) {
    await this.save(['location'], {
      location: { ...this.hero.location, building: building as string | undefined },
    });
  }

  @action
  async setActionTime() {
    if (this.hero) {
      setActionTime(this.hero);
      await this.save(['actionTime']);
    }
  }

  @action
  async checkCombatBotsTime() {
    const { combatBots } = this.hero;

    const TIME_DELTA = 6;

    if ((Date.now() - (combatBots.started || 0)) / 1000 / 60 / 60 > TIME_DELTA) {
      combatBots.warriors = [];
      delete combatBots.started;
      await this.save(['combatBots']);
    }
  }

  async save(fields: string[], obj: Partial<WarriorType> = {}) {
    await saveWarrior(
      pick(Object.assign(toJS(this.hero), obj), ['id', 'onlineTime', 'actionTime', ...fields]),
    );
  }

  async updateStatus(value: boolean, force?: boolean | null) {
    const status = getStatus(value);
    if (status === this.hero.status && !force) return;
    this.hero.status = status;

    await this.save(['status']);
  }
}

export default new Hero();
